import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { mapActions, mapState } from "pinia";
import { useApp } from "../../../../App/useApp";
import moment from "moment";

import { IGetAgency } from "../../../../../services/agency.service";
import {
    IEmployeeFilterList,
    UserService,
    IGetCaseManagerResponse,
    ICaseManagerSearchPayload,
    ICaseManagerOption
} from "../../../../../services/user-service";
import { ApplicationEnum, ApplicationNameEnum } from "../../../../../enums/ApplicationEnum";
import { PermissionManager } from "../../../../../common/utilities/permission-manager";
import { ParentPermissionEnum } from "../../../../../enums/ParentPermissionEnum";
import { PermissionActionEnum } from "../../../../../enums/PermissionActionEnum";
import { enumToMap } from "../../../../../common/vue-helpers/modifiers/enum-modifiers";
import { CalendarViewTypeEnum } from "../../../../../enums/CalendarViewTypeEnum";
import { ITypeaheadOptions } from "../../../../../resources-vue/vue-interfaces/i-typeahead";
import { IEnumResponse } from "../../../../../resources-vue/vue-interfaces/i-enum";
import { IMultiSelectDropDownOption } from "../../../../../resources-vue/vue-interfaces/i-grouped-multiselect";
import { IVisitMapDetails, IVisitMapRequestParams } from "../../../../../resources-vue/vue-interfaces/i-visit-map";
import { EnumsService } from "../../../../../services/enums.service";
import eventBus from "../../../../../utilities/eventBus";
import config from "../../../../../common/config";
import { PatientsScheduledFilter } from "../../../../../enums/PatientsScheduledFilterEnum";
import { GetProductClass } from "../../../../../resources/value-converters/get-product-class";
import { debounce } from "../../../../../common/vue-helpers/debounce";
import { useLoadingIndicator } from "../../../../Common/LoadingIndicator/store/useLoadingIndicator";
import { TaskService } from "../../../../../services/task.service";
import { AxxessIntelligenceService } from "../../../../../services/axxess-intelligence.service";
import { formatFullAddress } from "../../../../../common/vue-helpers/modifiers/value-modifier";
import { isMobileScreenSize } from "../../../../../utilities/isMobileScreenSize";
import { AnalyticsCategory, AnalyticsService } from "../../../../../services/analytics.service";
import { helperTestingAgency } from "../../../../../common/vue-helpers/testing-agency-check";

import VisitStatusMultiSelect from "../../../components/VisitStatusMultiSelect/VisitStatusMultiSelect.vue";
import MultiSelect from "../../../../../resources-vue/vue-elements/MultiSelect/MultiSelect.vue";
import MultiSelectFilter from "../../../../../resources-vue/vue-elements/MultiSelectFilter/MultiSelectFilter.vue";
import TypeaheadInput from "../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import BranchDefaultFilter from "../../../../../resources-vue/vue-custom-elements/BranchDefaultFilter/BranchDefaultFilter.vue";
import EmployeeVisitMap from "../EmployeeVisitMap/EmployeeVisitMap.vue";
import PatientVisitMap from "../../../PatientCalendar/components/PatientVisitMap/PatientVisitMap.vue";
import PatientProfile from "../../../../../resources-vue/vue-dialogs/PatientProfile/PatientProfile.vue";
import DailyDateNavigation from "../../../../../resources-vue/vue-custom-elements/DailyDateNavigation/DailyDateNavigation.vue";

export default defineComponent({
    components: {
        MultiSelect,
        MultiSelectFilter,
        TypeaheadInput,
        DailyDateNavigation,
        VisitStatusMultiSelect,
        BranchDefaultFilter,
        EmployeeVisitMap,
        PatientVisitMap
    },

    props: {
        listView: { type: String },
        isEmployeeCalendar: {
            type: Boolean,
            default: false
        },
        isPatientCalendar: {
            type: Boolean,
            default: false
        },
        handleViewChange: { type: Function }
    },

    data(vm) {
        return {
            _taskService: null as TaskService,
            _userService: null as UserService,
            _enumsService: null as EnumsService,
            _permissionManager: null as PermissionManager,
            _analyticsService: null as AnalyticsService,
            _axxessIntelligenceService: null as AxxessIntelligenceService,
            organizationName: "",
            selectedPatientStatus: null as number,
            agencyMultiSelectOptions: {} as IMultiSelectDropDownOption,
            selectedAgencies: [] as string[],
            resetToSelectAllBranches: false as boolean,
            selectedBranches: [] as string[],
            agencyList: [] as IGetAgency[],
            PatientScheduledFilterEnums: PatientsScheduledFilter,
            credentialsList: [] as string[],
            selectedCredentials: [] as ITypeaheadOptions[],
            patientStatusEnums: [] as IEnumResponse[],
            selectedEmployees: [] as any[],
            selectedVisitStatus: [] as string[],
            statusList: [
                { name: "Not Yet Started", value: "notYetStarted" },
                { name: "In Progress", value: "inProgress" },
                { name: "Completed", value: "completed" }
            ] as ITypeaheadOptions[],
            CalendarViewTypeEnum,
            selectedPatients: [] as any[],
            selectedLocations: [],
            allUsers: [] as IEmployeeFilterList[],
            selectedAgenciesName: [] as string[],
            caseManagerList: [] as string[],
            selectedCaseManagers: [] as ICaseManagerOption[],
            employees: [] as any[],
            productClass: new GetProductClass(),
            employeesLoading: false as boolean,
            mapDate: new Date(Date.now()) as Date,
            mapFilterOptions: {
                visitStatus: [],
                date: new Date(Date.now()).toISOString()
            } as IVisitMapRequestParams,
            visitMapDate: null as any,
            mapVisitsList: [] as any,
            employeesList: [] as any,
            patientsList: [] as any,
            fetchUpdatedCount: 0 as number,
            locationsList: {} as any,
            isMapVisitsListLoading: false as boolean,
            hideFilters: false as boolean,
            isClearFilters: false as boolean,
            isPremiumByAgency: null,
            isBranchesloaded: false,
            dialogRef: null,
            enumToMap,
            ApplicationNameEnum,
            isMobileScreen: false as boolean,
            hideMapLeftFilters: false as boolean,
            hasTestAgency: false as boolean
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    created() {
        this._enumsService = Container.instance.get(EnumsService);
        this._permissionManager = Container.instance.get(PermissionManager);
        this._userService = Container.instance.get(UserService);
        this._taskService = Container.instance.get(TaskService);
        this._analyticsService = Container.instance.get(AnalyticsService);
        this._axxessIntelligenceService = Container.instance.get(AxxessIntelligenceService);
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    async mounted() {
        this.showLoading(true);
        this.hasTestAgency = await helperTestingAgency.checkHasTestingAgency();
        this.isMobileScreen = isMobileScreenSize();
        this.credentialsList = await this._userService.getUserCredentials();
        this.organizationName = window.localStorage.getItem(config.organizationName);
        this.agencyList = this.organizations.find(
            (organization: { name: string }) => organization.name === this.organizationName
        ).agencies;
        this.agencyList = this.agencyList.map((agency) => {
            const agencyAndApplicationIds = agency.id.split("_");
            let application = parseInt(agencyAndApplicationIds[1], 10);
            if (application == 256) {
                application = ApplicationEnum.AxxessHospice;
            }
            return {
                ...agency,
                application: application
            };
        });
        await this.setAgencyMultiSelect();
        if (this.isPatientCalendar) {
            let patientStatusEnums = await this._enumsService.getPatientStatus();
            this.patientStatusEnums = patientStatusEnums.getJson();
            this.selectedPatientStatus = patientStatusEnums.getEnumValue("active") as number;
            const agenciesList = [...new Set(this.agencyList.map((agncy) => agncy.id.split("_")[0]))];
            const resps = await this._axxessIntelligenceService.getAiPermissionOfAgencies({
                AgencyIds: agenciesList
            });
            this.isPremiumByAgency = resps?.PremiumStatuses;
        }
        if (this.$route.query.agencies) {
            const agencies = (this.$route.query.agencies as string).split(",");
            this.updateBranches(agencies);
        }
        if (this.$route.query.credentials) {
            const credentials = (this.$route.query.credentials as string).split(",");
            const selectedCredentials = credentials.map((cred: any) => {
                return {
                    name: cred.split("_")[0],
                    value: cred.split("_")[1]
                };
            });
            this.selectedItemsUpdated(selectedCredentials);
        }
        if (this.$route.query.status) {
            const statuses = (this.$route.query.status as string).split(",");
            this.selectedVisitStatusUpdated(statuses);
        }

        if (this.$route.query.patientStatus) {
            const statuses = this.$route.query.patientStatus as string;
            this.selectedPatientStatus = Number(statuses);
            this.handlePatientStatusChange();
        }
    },

    watch: {
        isBranchesloaded: async function (newValue, oldValue) {
            if (newValue && newValue !== oldValue) {
                if (this.isEmployeeCalendar) {
                    await this.emitMapRefresh();
                } else {
                    await this.emitPatientMapRefresh();
                }
            }
        }
    },

    methods: {
        ...mapActions(useLoadingIndicator, { showLoading: "SET_LOADING" }),
        async setAgencyMultiSelect() {
            try {
                this.agencyMultiSelectOptions = {};
                this.selectedAgencies.length = 0;
                if (!this.agencyList || this.agencyList?.length === 0) {
                    return;
                }
                const allowedProducts = [
                    ApplicationEnum.AgencyCore,
                    ApplicationEnum.HomeCare,
                    ApplicationEnum.AxxessHospice,
                    ApplicationEnum.AxxessPalliative
                ];
                const schedulerViewPermissions = this._permissionManager.getPermmission(
                    ParentPermissionEnum.scheduler,
                    PermissionActionEnum.canView
                );
                const schedulerAgencies = schedulerViewPermissions
                    .filter((item: any) => {
                        if (!item) {
                            return false;
                        }
                        let application = item.application;
                        if (application === ApplicationEnum.AxxessHospiceFE) {
                            application = ApplicationEnum.AxxessHospice;
                        }
                        return allowedProducts.includes(application);
                    })
                    .map((x: any) => x.agencyId);
                const filterableProducts = this.agencyList.filter((agency) => {
                    const isAllowed = allowedProducts.includes(agency.application);
                    const agencyId = agency.id?.split("_")[0];
                    return isAllowed && schedulerAgencies.includes(agencyId);
                });
                filterableProducts?.forEach((agency) => {
                    const appName = ApplicationNameEnum[agency.application];

                    if (!this.agencyMultiSelectOptions[appName]) {
                        this.agencyMultiSelectOptions[appName] = [];
                    }

                    this.agencyMultiSelectOptions[appName].push({
                        displayText: agency.name,
                        value: agency.id
                    });
                });

                Object.values(this.agencyMultiSelectOptions).forEach((agency: any) => {
                    const values = agency.map((item: any) => item.value);
                    this.selectedAgencies.push(...values);
                });
                // For select agencies change
            } catch (e) {
                console.warn(e);
                // Silent fail
            }
        },

        selectEmployeeFetch(filter: string) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralDashboard,
                action: "Central Employees Search"
            });
            this.employees = Object.keys(this.employeesList).map((name: string) => {
                let details = this.employeesList[name];
                return {
                    name: details.name,
                    value: details.id,
                    agencyName: details.agencyName,
                    application: details.application,
                    credential: details.credential
                };
            });
            return this.employees.filter(
                (emp) => !filter || emp.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
        },

        selectedEmployeesUpdated(options: any, e?: any) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralDashboard,
                action: "Central Employees Search"
            });
            if (e && e.target.closest(".btn")) {
                return;
            }
            this.selectedEmployees = options;
            const mapModifiedEmployees = options.map((emp: any) => {
                const nameSplit = emp.name.split("+");
                const empName = `${nameSplit[0] + " " + nameSplit[1]}`;
                return {
                    ...emp,
                    name: empName
                };
            });
            eventBus.emit("highlightEmployeeMarker", mapModifiedEmployees);
        },

        async selectedVisitStatusUpdated(value: any) {
            this.selectedVisitStatus = value;
            const queryAgencies = this.$route.query.agencies;
            const queryCredentials = this.$route.query.credentials;
            const queryTitles = this.$route.query.titles;
            const querySchedule = this.$route.query.schedule;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    credentials: queryCredentials ? queryCredentials : "",
                    titles: queryTitles ? queryTitles : "",
                    schedule: querySchedule ? querySchedule : "",
                    status: this.selectedVisitStatus.join(",")
                }
            });
        },

        async clearFilters() {
            this.isClearFilters = true;
            this.isBranchesloaded = false;
            await this.setAgencyMultiSelect();
            this.resetToSelectAllBranches = !this.resetToSelectAllBranches;
            this.updateBranches(this.selectedAgencies);
            if (this.isEmployeeCalendar) {
                this.selectedCredentials = [];
                this.selectedVisitStatus = [];
                this.selectedItemsUpdated(this.selectedCredentials);
            } else {
                this.selectedPatientStatus = this.patientStatusEnums[0]?.value as number;
                this.selectedCaseManagers = null;
            }
            this.isClearFilters = false;
        },

        async emitMapRefresh() {
            this.selectedEmployees = [];
            this.mapRefresh({
                date: moment(this.mapDate).format("MM/DD/YYYY"),
                agencies: this.selectedAgencies,
                locationIds: this.selectedBranches,
                credentials: this.selectedCredentials?.map((credential) => credential.name).join(";"),
                visitStatus: this.selectedVisitStatus
            });
        },

        async emitPatientMapRefresh() {
            this.selectedPatients = [];
            await this.patientsMapRefresh({
                agencies: this.selectedAgencies,
                locationIds: this.selectedBranches,
                patientStatus: this.selectedPatientStatus,
                date: moment(this.mapDate).format("MM/DD/YYYY"),
                CaseManagerIds: this.selectedCaseManagers?.flatMap((cm) => cm.caseManagerIds || []) || []
            });
            if (this.selectedLocations.length) {
                this.updateSelectedLocations();
            }

            eventBus.emit("highlightPatientNameMarker", this.selectedPatients);
            eventBus.emit("highlightPatientLocationMarker", this.selectedLocations);
        },

        updateSelectedLocations() {
            this.selectedLocations = this.selectedLocations?.filter((location) => this.locationsList[location.name]);
        },

        updateBranches(branches: any) {
            this.selectedAgencies.length = 0;
            this.selectedAgencies.push(...branches);
            this.selectedAgenciesName = this.agencyList
                .filter((agency: any) => this.selectedAgencies.includes(agency.id))
                .map((agency: any) => agency.name);
            const queryAgencies = this.selectedAgencies.filter((_, index: number) => index < 12);
            const queryCredentials = this.$route.query.credentials;
            const queryTitles = this.$route.query.titles;
            const schedule = this.$route.query.schedule;
            const queryStatus = this.$route.query.status;
            const queryPatientStatus = this.$route.query.patientStatus;
            if (this.isEmployeeCalendar) {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies.join(","),
                        credentials: queryCredentials ? queryCredentials : "",
                        titles: queryTitles ? queryTitles : "",
                        schedule: schedule ? schedule : "",
                        status: queryStatus ? queryStatus : ""
                    }
                });
            } else {
                this.$router.replace({
                    query: {
                        agencies: queryAgencies.join(","),
                        schedule: schedule ? schedule : "",
                        patientStaus: queryPatientStatus ? queryPatientStatus : ""
                    }
                });
            }
        },

        userCredentialsFetch(filter: string) {
            return this.credentialsList
                .map((credential, index) => ({
                    name: credential,
                    value: index.toString()
                }))
                .filter((x) => !filter || x.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase()));
        },

        async caseManagerFetch(filter: string) {
            if (!filter || filter.length < 3) {
                return [];
            }
            const payload: ICaseManagerSearchPayload = {
                Term: filter.trim(),
                date: moment(this.mapDate).format("MM/DD/YYYY"),
                LocationIds: this.selectedBranches,
                Agencies: this.selectedAgencies,
                PatientStatus: this.selectedPatientStatus
            };
            const response = await this._userService.getCaseManager(payload);
            return response.map((user: IGetCaseManagerResponse) => {
                const caseManagerIds = user.CaseMagerIds.map((cm: any) => cm.Id || cm);
                return {
                    name: `${user.FirstName} ${user.LastName}`.trim(),
                    value: user.LoginId,
                    caseManagerIds: caseManagerIds
                };
            });
        },

        selectedCaseManagersUpdated(options: ICaseManagerOption[]) {
            this.selectedCaseManagers = options || [];
            const queryAgencies = this.$route.query.agencies;
            const allCaseManagerIds = this.selectedCaseManagers.flatMap((cm) => cm.caseManagerIds || []);
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    caseManagerIds: allCaseManagerIds.length ? allCaseManagerIds.join(",") : ""
                }
            });
        },

        selectedItemsUpdated(options: any) {
            this.selectedCredentials = options;
            const queryAgencies = this.$route.query.agencies;
            const queryTitles = this.$route.query.titles;
            const querySchedule = this.$route.query.schedule;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    credentials: this.selectedCredentials
                        ?.map((credential) => `${credential.name}_${credential.value}`)
                        .join(","),
                    titles: queryTitles ? queryTitles : "",
                    schedule: querySchedule ? querySchedule : ""
                }
            });
        },

        selectPatientFetch(filter: string) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralDashboard,
                action: "Central Patients Search"
            });
            return this.employees.filter(
                (emp) => !filter || emp.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
        },

        selectedPatientsUpdated(options: any, e?: any) {
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralDashboard,
                action: "Central Patients Search"
            });
            if (e && e.target.closest(".btn")) {
                return;
            }
            this.selectedPatients = options;
            const patients = this.selectedPatients.map((patient) => {
                const nameSplit = patient.name.split("+");
                const empName = `${nameSplit[0] + " " + nameSplit[1]}`;
                return empName;
            });
            eventBus.emit("highlightPatientNameMarker", patients);
        },

        handleBranchesOnChange(branches: any) {
            this.selectedBranches = branches;
            if (!this.isBranchesloaded && this.selectedBranches.length > 0) {
                this.isBranchesloaded = true;
            }
        },
        async mapDateUpdate(options: any, isWeekRangeChanged: boolean = true) {
            this.mapDate = options.startDate;
            if (this.isBranchesloaded && !this.isClearFilters) {
                if (this.isEmployeeCalendar) {
                    debounce(this.emitMapRefresh, 1200)();
                } else {
                    debounce(this.emitPatientMapRefresh, 1200)();
                }
            }
        },

        async mapRefresh(options: IVisitMapRequestParams) {
            this.mapFilterOptions = options;
            this.visitMapDate = options.date;
            this.showLoading(true);
            this.isMapVisitsListLoading = true;
            if (this.mapFilterOptions.agencies.length != 0 && this.mapFilterOptions.locationIds.length !== 0) {
                this.mapVisitsList = await this._taskService.getCalendarMapVisits(this.mapFilterOptions);
            } else {
                this.mapVisitsList = [];
            }
            this.showLoading(false);
            this.isMapVisitsListLoading = false;

            this.employeesList = {};
            for (let visit of this.mapVisitsList) {
                let name: any = visit.EmployeeName?.FirstName + " " + visit.EmployeeName?.LastName;
                name = name != " " ? name : "Unassigned";
                if (!this.employeesList[`${name}+${visit.Application}`]) {
                    this.employeesList[`${name}+${visit.Application}`] = {
                        name: `${name}+${visit.Application}`,
                        id: visit.Id,
                        agencyName: visit.AgencyName,
                        application: visit.Application === 256 ? ApplicationEnum.AxxessHospice : visit.Application,
                        credential: visit.EmployeeCredential
                    };
                }
            }
            this.fetchUpdatedCount++;
        },
        async patientsMapRefresh(options: IVisitMapRequestParams) {
            this.mapFilterOptions = { ...options, visitStatus: [] };
            this.visitMapDate = options.date;
            this.isMapVisitsListLoading = true;
            this.showLoading(true);
            if (this.mapFilterOptions.agencies.length != 0 && this.mapFilterOptions.locationIds.length !== 0) {
                const visitsList = await this._taskService.getCalendarMapVisits(this.mapFilterOptions);
                this.filterVisitsBySelectedLocation(visitsList);
            } else {
                this.mapVisitsList = [];
            }
            this.showLoading(false);
            this.isMapVisitsListLoading = false;

            this.patientsList = {};
            this.locationsList = {};
            this.employeesList = {};
            this.employees = [];
            for (let visit of this.mapVisitsList) {
                const name: any = visit.PatientName.FirstName + " " + visit.PatientName.LastName;
                const address: any = formatFullAddress(visit.PatientAddress);
                if (!this.employeesList[`${name}+${visit.Application}`]) {
                    this.employeesList[`${name}+${visit.Application}`] = {
                        name: `${name}+${visit.Application}`,
                        id: visit.Id,
                        agencyName: visit.AgencyName,
                        application: visit.Application === 256 ? ApplicationEnum.AxxessHospice : visit.Application,
                        agencyId: visit.AgencyId,
                        patientId: visit.PatientId
                    };
                }
                this.employees = Object.keys(this.employeesList).map((emp: string) => {
                    let details = this.employeesList[emp];
                    return {
                        name: details.name,
                        value: details.id,
                        agencyName: details.agencyName,
                        application: details.application,
                        agencyId: details.agencyId,
                        patientId: details.patientId
                    };
                });

                if (!this.patientsList[name]) {
                    this.patientsList[name] = visit.PatientId + "_" + address;
                }
                if (!this.locationsList[address]) {
                    this.locationsList[address] = visit.Id;
                }
            }
            this.fetchUpdatedCount++;
        },

        filterVisitsBySelectedLocation(allVisits: IVisitMapDetails[]) {
            if (!this.selectedLocations.length) {
                this.mapVisitsList = allVisits;
                return;
            }
            // filter visits list by selected location to display only visits in selected location
            const visitsList = allVisits?.filter(
                (visit) => this.selectedLocations.findIndex((loc) => loc.value === visit.Id) !== -1
            );

            // if there are visits in selected location, only display those visits, else display all visits returned from BE
            this.mapVisitsList = visitsList?.length ? visitsList : allVisits;
        },

        selectLocationFetch(filter: string) {
            const locations = [];
            for (const [key, value] of Object.entries(this.locationsList)) {
                locations.push({ name: key, value: value });
            }
            return locations.filter(
                (emp) => !filter || emp.name.toLocaleLowerCase().includes(filter.toLocaleLowerCase())
            );
        },
        selectedLocationsUpdated(options: any) {
            this.selectedLocations = options;
            const locations = this.selectedLocations.map((loc) => loc.name);
            eventBus.emit("highlightPatientLocationMarker", locations);
        },
        async openTaskManager(e: any, employee: any) {
            e.stopPropagation();
            // will be implemented later
            // This has to be done because backend is sending -3 for hospice solution.
            let application;
            if (employee.application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = employee.application;
            }
            const visitDetails = this.mapVisitsList.find((visit: any) => visit.Id === employee.value);
            this.$router.push({
                path: "/axxess-central/task-manager?",
                query: {
                    agencyId: visitDetails.AgencyId,
                    fullname: `${visitDetails.EmployeeName.FirstName} ${visitDetails.EmployeeName.LastName} ${visitDetails.EmployeeName.Credentials ? visitDetails.EmployeeName.Credentials : ""}`,
                    application: application,
                    userId: visitDetails.UserId
                }
            });
            this._analyticsService.logEvent({
                category: AnalyticsCategory.CentralCalendarNavigation,
                action: "Central Employee calendar Map EmployeeTaskMgr"
            });
        },
        async openPatientTaskManager(e: any, patient: any) {
            e.stopPropagation();
            let application;
            if (patient.application === ApplicationEnum.AxxessHospice) {
                application = ApplicationEnum.AxxessHospiceFE;
            } else {
                application = patient.application;
            }
            const visitDetails = this.mapVisitsList.find((visit: any) => visit.Id === patient.value);
            this.$router.push({
                path: "/axxess-central/task-manager-patient?",
                query: {
                    agencyId: visitDetails.AgencyId,
                    fullname: `${visitDetails.PatientName.FirstName} ${visitDetails.PatientName.LastName}`,
                    application: application,
                    patientId: visitDetails.PatientId,
                    branchId: visitDetails.LocationId
                }
            });
        },
        async openProfile(e: any, patient: any) {
            e.stopPropagation();
            const patientData = {
                AgencyId: patient.agencyId,
                Application: patient.application,
                Id: patient.patientId
            };
            this.dialogRef = this.$dialog.open(PatientProfile, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 750px"
                },
                data: {
                    patientData
                }
            });
        },
        isSelected(id) {
            if (this.isPatientCalendar) {
                return this.selectedPatients.some((emp) => emp.value === id);
            } else {
                return this.selectedEmployees.some((emp) => emp.value === id);
            }
        },
        handlePatientStatusChange() {
            const queryAgencies = this.$route.query.agencies;
            const querySchedule = this.$route.query.schedule;
            this.$router.replace({
                query: {
                    agencies: queryAgencies ? queryAgencies : "",
                    schedule: querySchedule ? querySchedule : "",
                    patientStatus: this.selectedPatientStatus
                }
            });
        },
        toggleMapLeftFilters(): void {
            this.hideMapLeftFilters = !this.hideMapLeftFilters;
        }
    }
});
